import { Fragment, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Menu, Transition } from '@headlessui/react'
import {
  Bars3Icon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  UserIcon,
  ChevronDownIcon,
  CheckIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { useShowTestData } from '@/contexts/ShowTestDataContext'
import { getUserRoleFlags } from '@/components/layout/navigationSections'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { impersonationApi } from '@/services/api'
import { ImpersonationModal } from '@/components/impersonation/ImpersonationModal'
import { useI18n } from '@/contexts/I18nContext'
import { LANGUAGES, LanguageCode } from '@/i18n/config'
import { Logo } from '../ui/Logo'
import { BRAND_COLORS } from '@/styles/chartPalette'
import clsx from 'clsx'

interface HeaderProps {
  onMenuClick: () => void
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user: authUser, logout, isAuthenticated } = useAuth()
  const { isImpersonating, startImpersonation } = useImpersonation()
  const { showTestData, setShowTestData } = useShowTestData()
  const { t } = useTranslation('common')
  const { language, changeLanguage } = useI18n()
  const [impersonationModalOpen, setImpersonationModalOpen] = useState(false)
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false)

  // Auto-hide on scroll-down, reveal on scroll-up (Headroom pattern).
  // User wanted the top bar to reappear on any small upscroll, not
  // only when the page is scrolled all the way back to the top.
  // Scroll-down past 80 px hides the bar; any upward scroll reveals
  // it; the bar always shows when within 80 px of the top so it
  // never feels 'lost' near the page top.
  const [hidden, setHidden] = useState(false)
  const lastScrollY = useRef(0)
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY
      const dy = y - lastScrollY.current
      // Ignore tiny jitter (sub-pixel scroll, momentum end), keeps
      // the bar from flickering on touchscreen flick decay.
      if (Math.abs(dy) < 8) return
      if (y < 80) {
        setHidden(false)
      } else if (dy > 0) {
        setHidden(true)   // scrolling down
      } else {
        setHidden(false)  // scrolling up
      }
      lastScrollY.current = y
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // #1799: read the user from AuthProvider only. Header used to run its own
  // observer on the profile query (seeded with `initialData: authUser`,
  // enabled by `isAuthenticated`). Logout disables AuthProvider's query and
  // clears the cache (#1746), but a Header render landing before the LOGOUT
  // dispatch rebuilt the query with the old user as data, which signed the
  // user straight back in. AuthProvider already writes every profile it gets
  // (bootstrap, login, updateUser, refreshUser) into its state.
  const user = authUser

  // Real actor is admin when impersonating (impersonation is admin-only) or getUserRoleFlags says so.
  const realIsAdmin = isImpersonating || getUserRoleFlags(user).isAdmin

  // Recent-impersonations shortcut list. Fetched once when an admin
  // signs in (and refreshed whenever the menu re-opens via React
  // Query's window-focus refetch). Hidden for non-admins and while
  // already impersonating — the menu rule on the existing
  // "Impersonate User" item.
  const { data: recentImpersonationsResp } = useQuery({
    queryKey: ['impersonation', 'recent'],
    queryFn: () => impersonationApi.getRecentImpersonations(5),
    enabled: realIsAdmin && !isImpersonating && isAuthenticated,
    staleTime: 60 * 1000, // 1 min — fresh enough for the menu's purpose
  })
  const recentTargets = (recentImpersonationsResp as any)?.data?.users ?? []

  // Click-to-impersonate from the recents list. ImpersonationContext
  // already handles token swap + reload — mirror what
  // ImpersonationModal does (see ImpersonationModal.tsx:43).
  const impersonateNow = async (userId: string) => {
    try {
      await startImpersonation(userId, t('impersonateUser', 'Admin impersonation'))
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('recent-impersonation failed:', err)
    }
  }

  return (
    <header className={clsx(
      'sticky top-0 z-50 w-full bg-white border-b border-neutral-200 shadow-sm',
      'transition-transform duration-200 ease-out',
      hidden && '-translate-y-full'
    )}>
      <div className="flex h-14 items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Left side in LTR / Right side in RTL - Menu button (mobile only) */}
        <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
          {/* Hamburger menu button - only visible on mobile/tablet.
              `-ms-2` pulls the button against the start edge on
              mobile (logical property — left in LTR, right in RTL)
              so it sits flush at the viewport edge instead of
              floating ~16 px in behind the parent's `px-4` padding.
              `sm:ms-0` returns to normal spacing once tablet sizing
              gives the bar more breathing room. */}
          <button
            type="button"
            className="lg:hidden -ms-2 sm:ms-0 p-2 text-neutral-700 hover:text-neutral-900 hover:bg-neutral-100 rounded-md transition-colors duration-200 flex-shrink-0"
            onClick={onMenuClick}
          >
            <span className="sr-only">{t('openMenu')}</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
          {/* Brand mark on mobile/tablet only — at <lg the sidebar is
              collapsed into a drawer, leaving the top bar bare. Drop
              a small Logo here so the page always carries the brand
              identity. Hidden at lg+ where the sidebar's full logo
              already shows. */}
          <Link to="/" className="lg:hidden flex-shrink-0" aria-label="Hoteliate home">
            <Logo size="sm" />
          </Link>
          {/* #683 §1.4: brand slogan moved out of the sidebar. Left-pinned,
              lg+ only, intrinsically LTR (#602 — never translated). */}
          <p className="hidden lg:block text-xs italic text-neutral-500 truncate" dir="ltr">Your Gateway to Alternative Hotel Growth</p>
        </div>

        {/* Right side in LTR / Left side in RTL - Language Toggle and Profile */}
        <div className="flex items-center gap-3 sm:gap-4">
          {/* Settings Icon */}
          <Link
            to="/settings"
            className="flex items-center justify-center h-9 w-9 rounded-lg text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 transition-all duration-150"
            aria-label={t('settings', 'Settings')}
          >
            <Cog6ToothIcon className="h-5 w-5" />
          </Link>

          {/* #683 §1.5: Help moved from the sidebar footer to the header
              as an icon-only mailto, placed after the settings gear. */}
          <a
            href="mailto:support@hoteliate.com"
            aria-label={t('common:help')}
            className="flex items-center justify-center h-9 w-9 rounded-lg text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100"
          >
            <QuestionMarkCircleIcon className="h-5 w-5" />
          </a>

          {/* Language Dropdown */}
          <div className="relative">
            <button
              onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
              className="flex items-center gap-1.5 h-9 px-3 rounded-lg text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 transition-all duration-150"
              aria-label={t('changeLanguage', 'Change language')}
            >
              <span className="text-base leading-none">{LANGUAGES[language]?.flag}</span>
              <span className="text-sm font-normal hidden sm:inline">{LANGUAGES[language]?.nativeName}</span>
              <ChevronDownIcon className={clsx(
                "h-4 w-4 transition-transform duration-150",
                languageDropdownOpen && "rotate-180"
              )} />
            </button>

            {/* Language Dropdown Menu */}
            {languageDropdownOpen && (
              <>
                {/* Backdrop to close dropdown */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setLanguageDropdownOpen(false)}
                />
                <div className="absolute end-0 z-50 mt-2 w-56 max-h-80 overflow-y-auto bg-white rounded-lg shadow-lg border border-neutral-200 py-1">
                  {(Object.keys(LANGUAGES) as LanguageCode[]).map((code) => {
                    const lang = LANGUAGES[code]
                    return (
                      <button
                        key={code}
                        onClick={() => {
                          changeLanguage(code)
                          setLanguageDropdownOpen(false)
                        }}
                        className={clsx(
                          "w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150",
                          language === code
                            ? "text-primary-600 bg-primary-50 font-medium"
                            : "text-neutral-700 hover:bg-neutral-50"
                        )}
                      >
                        <span className="text-base leading-none">{lang.flag}</span>
                        <span className="flex-1 text-start">{lang.nativeName}</span>
                        {language === code && (
                          <CheckIcon className="h-4 w-4 text-primary-600" />
                        )}
                      </button>
                    )
                  })}
                </div>
              </>
            )}
          </div>

          {/* Profile dropdown */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center gap-2 p-2 text-neutral-700 hover:text-neutral-900 hover:bg-neutral-100 rounded-md transition-colors duration-200">
              <span className="sr-only">{t('openUserMenu')}</span>
              {user?.avatarUrl || user?.profilePicture ? (
                <img
                  className="h-8 w-8 rounded-full object-cover bg-neutral-50 border-2 border-neutral-200"
                  src={user.avatarUrl || user.profilePicture}
                  alt=""
                />
              ) : (
                <div
                  className="h-8 w-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: BRAND_COLORS.lightBlue }}
                >
                  <UserCircleIcon className="h-6 w-6" style={{ color: BRAND_COLORS.primary }} />
                </div>
              )}
              <span className="hidden sm:block text-sm font-medium text-neutral-900">
                {user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : (user as any)?.name || user?.email}
              </span>
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute end-0 z-10 mt-2 w-64 origin-top-end rounded-lg bg-white py-2 shadow-lg ring-1 ring-neutral-900/5 focus:outline-none">
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/profile"
                      className={clsx(
                        active ? 'bg-neutral-50' : '',
                        'block px-4 py-2 text-sm text-neutral-900 flex items-center'
                      )}
                    >
                      <UserCircleIcon className="h-4 w-4 me-3" />
                      {t('yourProfile')}
                    </Link>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/settings"
                      className={clsx(
                        active ? 'bg-neutral-50' : '',
                        'block px-4 py-2 text-sm text-neutral-900 flex items-center'
                      )}
                    >
                      <Cog6ToothIcon className="h-4 w-4 me-3" />
                      {t('settings')}
                    </Link>
                  )}
                </Menu.Item>

                {/* Admin-only: recently-impersonated shortcuts.
                    One click on a row re-impersonates that user
                    without opening the full picker modal. List is
                    capped at 5 from the backend; hidden entirely
                    while the admin is already impersonating
                    somebody. */}
                {realIsAdmin && !isImpersonating && recentTargets.length > 0 && (
                  <>
                    <div className="border-t border-neutral-100 my-1" />
                    <div className="px-4 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-neutral-400">
                      {t('recentImpersonations', 'Recent impersonations')}
                    </div>
                    {recentTargets.map((u: any) => {
                      const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email
                      return (
                        <Menu.Item key={u.id}>
                          {({ active }) => (
                            <button
                              onClick={() => impersonateNow(u.id)}
                              className={clsx(
                                active ? 'bg-neutral-50' : '',
                                'w-full text-start block px-4 py-2 text-sm text-neutral-900 flex items-center'
                              )}
                              title={u.email}
                            >
                              <UserIcon className="h-4 w-4 me-3 flex-shrink-0 text-neutral-400" />
                              <span className="flex-1 min-w-0 truncate">
                                <span className="font-medium">{full}</span>
                                <span className="ms-1 text-xs text-neutral-500">· {u.userType}</span>
                              </span>
                            </button>
                          )}
                        </Menu.Item>
                      )
                    })}
                  </>
                )}

                {/* Admin-only: Impersonate User option (hidden while already impersonating) */}
                {realIsAdmin && !isImpersonating && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => setImpersonationModalOpen(true)}
                        className={clsx(
                          active ? 'bg-neutral-50' : '',
                          'w-full text-start block px-4 py-2 text-sm text-neutral-900 flex items-center'
                        )}
                      >
                        <UserIcon className="h-4 w-4 me-3" />
                        {recentTargets.length > 0
                          ? t('impersonateAnotherUser', 'Impersonate another user…')
                          : t('impersonateUser')}
                      </button>
                    )}
                  </Menu.Item>
                )}

                {/* Admin-only: Show test data toggle */}
                {realIsAdmin && (
                  <>
                    <div className="border-t border-neutral-100 my-1" />
                    <div className="px-4 py-2 flex items-center justify-between gap-3">
                      <span className="text-sm text-neutral-700">{t('showTestData', 'Show test data')}</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={showTestData}
                        onClick={() => setShowTestData(!showTestData)}
                        className={clsx(
                          'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                          showTestData ? 'bg-primary-600' : 'bg-neutral-200'
                        )}
                      >
                        <span
                          aria-hidden="true"
                          className={clsx(
                            'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                            showTestData ? 'translate-x-4' : 'translate-x-0'
                          )}
                        />
                      </button>
                    </div>
                  </>
                )}

                <div className="border-t border-neutral-100 my-1" />

                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={logout}
                      className={clsx(
                        active ? 'bg-neutral-50' : '',
                        'w-full text-start block px-4 py-2 text-sm flex items-center'
                      )}
                      style={{ color: BRAND_COLORS.secondary }}
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4 me-3" />
                      {t('signOut')}
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>

      {/* Impersonation Modal */}
      <ImpersonationModal
        isOpen={impersonationModalOpen}
        onClose={() => setImpersonationModalOpen(false)}
      />
    </header>
  )
}
